import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Button,
  Tooltip,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/RestoreFromTrash";
import AxiosInstance from "service/axiosInstance";
import { useRestoreTransaction, useGetAllTransactionTypes } from "pages/Dashboard/dashboardQueries";

const modeOfPayment = [
  "Cash",
  "Debit Card",
  "UPI (Google pay/Phone Pe)",
  "Mobile Wallets",
  "Credit (at the shop)",
  "Credit Card",
  "Bank Transfer",
  "Net Banking",
  "Any other",
];

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString();
}

const AuditLogDetailsModal = ({ open, onClose, log, currentTransaction }) => {
  if (!log) return null;

  const getActionType = () => {
    const type = log.eventType || "EDITED";
    if (type === "DELETED") {
      return { label: "DELETED", color: "error" };
    }
    if (type === "RESTORED") {
      return { label: "RESTORED", color: "success" };
    }
    return { label: "EDITED", color: "info" };
  };

  const action = getActionType();
  const isDeletedOrRestored = action.label === "DELETED" || action.label === "RESTORED";
  const themeColor = action.label === "DELETED" ? "#ffebee" : "#e8f5e9";

  const checkIsEdited = (before, after) => {
    if (!currentTransaction) return false;
    const b = (before === null || before === undefined) ? "" : String(before).trim();
    const a = (after === null || after === undefined) ? "" : String(after).trim();
    return b !== a;
  };

  const isAmountEdited = checkIsEdited(log.amount, currentTransaction?.amount);
  const isTypeEdited = checkIsEdited(log.type, currentTransaction?.type);
  const isCategoryEdited = checkIsEdited(log.subCategory, currentTransaction?.subCategory);
  const isModeEdited = checkIsEdited(log.mode, currentTransaction?.mode);
  const isSubCategoryEdited = checkIsEdited(log.description, currentTransaction?.description);
  const isPurposeEdited = checkIsEdited(log.purpose, currentTransaction?.purpose);
  
  const isDateEdited = currentTransaction 
    ? formatDateTime(log.transactionDate) !== formatDateTime(currentTransaction.transactionDate) 
    : false;

  const getBeforeBg = (isEdited) => isEdited ? "#ffebee" : "#f5f5f5";
  const getAfterBg = (isEdited) => isEdited ? "#e8f5e9" : "#f5f5f5";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          Audit Log Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Action</Typography>
            <Chip label={action.label} color={action.color} size="small" />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Transaction ID</Typography>
            <Typography variant="body1" fontWeight={600}>{log.transactionId}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Employee ID</Typography>
            <Typography variant="body1" fontWeight={600}>{log.employeeId || "-"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>User</Typography>
            <Typography variant="body1">{log.editorName || "Unknown"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Timestamp</Typography>
            <Typography variant="body1">{formatDateTime(log.createdAt)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
              {isDeletedOrRestored ? "Transaction Details" : "Changes Made"}
            </Typography>
          </Grid>
          {isDeletedOrRestored ? (
            <>
              <Grid item xs={6}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amount</Typography>
                  <Typography variant="h6" fontWeight={600}>₹{log.amount}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Type</Typography>
                  <Chip label={log.type} size="small" color={log.type === "CREDIT" ? "success" : "error"} />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Category</Typography>
                  <Typography variant="body1" fontWeight={600}>{log.subCategory || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Mode of Payment</Typography>
                  <Typography variant="body1" fontWeight={600}>{log.mode || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Transaction Date</Typography>
                  <Typography variant="body1" fontWeight={600}>{formatDateTime(log.transactionDate)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sub Category</Typography>
                  <Typography variant="body1" fontWeight={600}>{log.description || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ bgcolor: themeColor, p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Purpose</Typography>
                  <Typography variant="body1" fontWeight={600}>{log.purpose || "N/A"}</Typography>
                </Box>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isAmountEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amount - Before</Typography>
                    <Typography variant="h6" fontWeight={600}>₹{log.amount}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isAmountEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amount - After</Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {currentTransaction?.amount ? `₹${currentTransaction.amount}` : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isTypeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Type - Before</Typography>
                    <Chip label={log.type} size="small" color={log.type === "CREDIT" ? "success" : "error"} />
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isTypeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Type - After</Typography>
                    {currentTransaction?.type ? (
                      <Chip label={currentTransaction.type} size="small" color={currentTransaction.type === "CREDIT" ? "success" : "error"} />
                    ) : "N/A"}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isCategoryEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Category - Before</Typography>
                    <Typography variant="body1">{log.subCategory || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isCategoryEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Category - After</Typography>
                    <Typography variant="body1">{currentTransaction?.subCategory || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isModeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Mode - Before</Typography>
                    <Typography variant="body1">{log.mode || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isModeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Mode - After</Typography>
                    <Typography variant="body1">{currentTransaction?.mode || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isSubCategoryEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sub Category - Before</Typography>
                    <Typography variant="body1">{log.description || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isSubCategoryEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Sub Category - After</Typography>
                    <Typography variant="body1">{currentTransaction?.description || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isPurposeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Purpose - Before</Typography>
                    <Typography variant="body1">{log.purpose || "N/A"}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isPurposeEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Purpose - After</Typography>
                    <Typography variant="body1">{currentTransaction?.purpose || "N/A"}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1, bgcolor: getBeforeBg(isDateEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Transaction Date - Before</Typography>
                    <Typography variant="body1">{formatDateTime(log.transactionDate)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, bgcolor: getAfterBg(isDateEdited), p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Transaction Date - After</Typography>
                    <Typography variant="body1">
                      {currentTransaction?.transactionDate ? formatDateTime(currentTransaction.transactionDate) : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const AuditLogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Track which transactionIds have been restored in this session (for optimistic UI)
  const [restoredIds, setRestoredIds] = useState(new Set());

  // Search and Filter states
  const [searchVal, setSearchVal] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mode, setMode] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");

  const { data: transactionTypesData } = useGetAllTransactionTypes();
  const transactionTypeOptions = transactionTypesData?.data?.length
    ? transactionTypesData.data
    : [
        { value: "CREDIT", label: "Inflow", isActive: true },
        { value: "DEBIT", label: "Outflow", isActive: true },
        { value: "NONE", label: "No Transaction", isActive: true },
      ];

  const { mutate: restoreTx, isLoading: isRestoring, variables: restoringId } = useRestoreTransaction();

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchVal);
      setPage(0);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchVal]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchVal("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setMode("");
    setType("");
    setStatus("");
    setPage(0);
  };

  const fetchAuditLogs = useCallback(async (currentPage, perPage, filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await AxiosInstance({
        method: "GET",
        url: `api/v1/admin/audit-logs`,
        params: {
          page: currentPage + 1,
          perPage,
          search: filters.search || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          mode: filters.mode || undefined,
          type: filters.type || undefined,
          status: filters.status || undefined,
        },
      });
      const payload = response?.data ?? {};
      setLogs(payload?.logs ?? []);
      setTotalCount(payload?.total ?? payload?.logs?.length ?? 0);
    } catch (err) {
      setError(err?.message || err?.error || "Failed to load audit logs.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCurrentTransaction = async (transactionId) => {
    try {
      const response = await AxiosInstance({
        method: "GET",
        url: `api/v1/admin/transaction/${transactionId}/audit-logs`,
      });
      return response?.data?.transaction || null;
    } catch (err) {
      console.error("Failed to fetch current transaction:", err);
      return null;
    }
  };

  const handleRowClick = async (log) => {
    setSelectedLog(log);
    setModalOpen(true);
    const transaction = await fetchCurrentTransaction(log.transactionId);
    setCurrentTransaction(transaction);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLog(null);
    setCurrentTransaction(null);
  };

  const handleRestore = (e, transactionId) => {
    // Prevent row click from opening modal
    e.stopPropagation();
    restoreTx(transactionId, {
      onSuccess: () => {
        // Optimistically mark as restored and refresh logs
        setRestoredIds((prev) => new Set(prev).add(transactionId));
        fetchAuditLogs(page, rowsPerPage, { search, startDate, endDate, mode, type, status });
      },
    });
  };

  useEffect(() => {
    fetchAuditLogs(page, rowsPerPage, { search, startDate, endDate, mode, type, status });
  }, [page, rowsPerPage, search, startDate, endDate, mode, type, status, fetchAuditLogs]);

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", p: 3, boxSizing: "border-box" }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Transaction Audit Logs
      </Typography>

      {/* Search and Filters */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 2, bgcolor: "#fafafa", p: 2, borderRadius: 1, border: "1px solid #e0e0e0" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Editor, Employee ID, Transaction ID"
              placeholder="Search..."
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
          </Grid>

          {/* Date Filter */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={handleFilterChange(setStartDate)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={handleFilterChange(setEndDate)}
            />
          </Grid>

          {/* Mode Filter */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Mode"
              value={mode}
              onChange={handleFilterChange(setMode)}
            >
              <MenuItem value="">All Modes</MenuItem>
              {modeOfPayment.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Type Filter */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Type"
              value={type}
              onChange={handleFilterChange(setType)}
            >
              <MenuItem value="">All Types</MenuItem>
              {transactionTypeOptions.map((t) => (
                <MenuItem
                  key={t.value}
                  value={t.value}
                  sx={t.isActive === false ? { color: "error.main" } : {}}
                >
                  {t.label}
                  {t.isActive === false && (
                    <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.7 }}>(hidden)</span>
                  )}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={status}
              onChange={handleFilterChange(setStatus)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="EDITED">Edited</MenuItem>
              <MenuItem value="DELETED">Deleted</MenuItem>
              <MenuItem value="RESTORED">Restored</MenuItem>
            </TextField>
          </Grid>

          {/* Reset Button */}
          <Grid item xs={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={handleResetFilters}
              sx={{ height: "40px", textTransform: "none", fontWeight: 600 }}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && logs.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          No audit log entries found.
        </Typography>
      )}

      {!loading && !error && logs.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Editor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Device ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date &amp; Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sub Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => {
                  const isDeletedTx = log.isDeleted && !restoredIds.has(log.transactionId);
                  const isCurrentlyRestoring = isRestoring && restoringId === log.transactionId;
                  return (
                    <TableRow
                      key={log.id ?? index}
                      onClick={() => handleRowClick(log)}
                      sx={{ "&:hover": { backgroundColor: "#fafafa", cursor: "pointer" } }}
                    >
                      <TableCell>{log.transactionId ?? "-"}</TableCell>
                      <TableCell>{log.employeeId ?? "-"}</TableCell>
                      <TableCell>{log.editorName || "Unknown"}</TableCell>
                      <TableCell>{log.deviceId || "-"}</TableCell>
                      <TableCell>{formatDateTime(log.createdAt)}</TableCell>
                      <TableCell>₹{log.amount ?? "-"}</TableCell>
                      <TableCell>
                        {log.type && (
                          <Chip
                            label={log.type}
                            size="small"
                            color={log.type === "CREDIT" ? "success" : log.type === "DEBIT" ? "error" : "default"}
                          />
                        )}
                      </TableCell>
                      <TableCell>{log.subCategory ?? "-"}</TableCell>
                      <TableCell>{log.mode ?? "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {log.description || "-"}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {log.purpose || "-"}
                      </TableCell>
                      <TableCell>{formatDateTime(log.transactionDate)}</TableCell>
                      <TableCell>
                        {isDeletedTx || log.eventType === "DELETED" ? (
                          <Chip label="Deleted" size="small" color="error" variant="outlined" />
                        ) : log.eventType === "RESTORED" ? (
                          <Chip label="Restored" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="Edited" size="small" color="info" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {isDeletedTx && (
                          <Tooltip title="Restore this transaction">
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<RestoreIcon />}
                              disabled={isCurrentlyRestoring}
                              onClick={(e) => handleRestore(e, log.transactionId)}
                              data-testid={`restore-btn-${log.transactionId}`}
                              sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 600 }}
                            >
                              {isCurrentlyRestoring ? "Restoring..." : "Restore"}
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      <AuditLogDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        log={selectedLog}
        currentTransaction={currentTransaction}
      />
    </Box>
  );
};

export default AuditLogsTable;
